// Environment configuration
// BASE_URL is determined by hostname for automatic environment detection.
//
// Client API uses host.json routePrefix "api" — HTTP routes are under /api/ (matches Azure invoke URLs).
//
// Tenant portals on {subdomain}.dev.avsight-ai.com do not encode the Azure slot in the hostname.
// For those hosts, set REACT_APP_CLIENT_API_BASE_URL at build time to the slot that holds the SF org
// (e.g. dev5-avsight-connect → https://dev5-av-connect-client-api.azurewebsites.net/api).
const normalizeBaseUrl = (url) => {
  const u = String(url).trim().replace(/\/+$/, '');
  return `${u}/`;
};

const getBaseUrl = () => {
  // CRA replaces `process.env.REACT_APP_*` references at build time via
  // Webpack DefinePlugin. The legacy `typeof process !== 'undefined'`
  // guard breaks in the browser because CRA v4+ no longer polyfills the
  // `process` global, so the guard short-circuits to false and we'd
  // silently fall back to the dev5 default for every tenant portal even
  // when the env var WAS set at build time. Read the env var directly.
  const envBase = process.env.REACT_APP_CLIENT_API_BASE_URL;
  if (envBase && String(envBase).trim()) {
    return normalizeBaseUrl(envBase);
  }

  const hostname = window.location.hostname;

  if (hostname === 'localhost') {
    return 'http://localhost:7071/api/';
  }

  // Dev slot environments. Supported host shapes (slot number is captured):
  //   - devN.dev.avsight-ai.com                 (bare slot subdomain)
  //   - devN<suffix>.dev.avsight-ai.com         (slot-scoped tenant portal, e.g. dev2acon)
  //   - <sub>.devN.dev.avsight-ai.com           (nested slot subdomain)
  //   - devN-av-connect-*                       (Azure resource hostnames)
  // The shared SWA serves one hostname-detecting bundle to many tenant portals, so a
  // slot-prefixed subdomain must resolve to that slot's Client API here rather than
  // falling through to the generic dev5 fallback below.
  const devSlotMatch =
    hostname.match(/^dev(\d+)[a-z0-9-]*\.dev\.avsight-ai\.com$/) ||
    hostname.match(/(?:^|\.)dev(\d+)\.dev\.avsight-ai\.com$/) ||
    hostname.match(/^dev(\d+)-av-connect/);
  if (devSlotMatch) {
    return `https://dev${devSlotMatch[1]}-av-connect-client-api.azurewebsites.net/api/`;
  }

  // Tenant portals on *.dev.avsight-ai.com (e.g. latte-lac.dev.avsight-ai.com) that are not
  // devN-style slots.  REACT_APP_CLIENT_API_BASE_URL should always be set at build time via
  // the Makefile deploy-client-interface-slot target — the envBase check above handles that.
  // This branch only fires when the build was done without the env var (e.g. local dev or
  // direct `npm run build` without the slot prefix).  We fall back to dev5 as the primary
  // dev slot; the correct URL is always `REACT_APP_CLIENT_API_BASE_URL` at build time.
  if (hostname.endsWith('.dev.avsight-ai.com')) {
    // eslint-disable-next-line no-console
    console.warn('[env] REACT_APP_CLIENT_API_BASE_URL not set — tenant portal falling back to dev5 Client API. Set this env var at build time for production deployments.');
    return 'https://dev5-av-connect-client-api.azurewebsites.net/api/';
  }

  // Shared dev SWA default hostname
  if (hostname === 'marketplace.dev.avsight-ai.com') {
    return 'https://dev-av-connect-client-api.azurewebsites.net/api/';
  }

  // Dev environment (Azure Static Web App: thankful-sand-*)
  if (hostname.includes('thankful-sand') || hostname.includes('dev-av-connect')) {
    return 'https://dev-av-connect-client-api.azurewebsites.net/api/';
  }

  // Staging environment (Azure Static Web App: victorious-rock-*)
  if (hostname.includes('victorious-rock') || hostname.includes('stgn-av-connect')) {
    return 'https://stgn-av-connect-client-api.azurewebsites.net/api/';
  }

  // Production environment (Azure Static Web App: kind-tree-*)
  if (hostname.includes('kind-tree') || hostname.includes('prod-av-connect')) {
    return 'https://prod-av-connect-client-api.azurewebsites.net/api/';
  }

  // Default fallback (shared dev Client API). For *.dev.avsight-ai.com tenant portals tied to a dev
  // slot, use REACT_APP_CLIENT_API_BASE_URL instead of relying on this default.
  return 'https://dev-av-connect-client-api.azurewebsites.net/api/';
};

const BASE_URL = getBaseUrl();

// Must match AUTH_BEARER_TOKEN on the target Client API (each slot has its own app settings).
// CRA replaces `process.env.REACT_APP_*` at build time; do NOT guard with
// `typeof process` — it's undefined in browsers under CRA v4+ and breaks
// the build-time replacement.
const BEARER_TOKEN =
  (process.env.REACT_APP_AUTH_BEARER_TOKEN && String(process.env.REACT_APP_AUTH_BEARER_TOKEN).trim()) ||
  'ceZP6Tocqc84TBmiFsgdpG4O0cSlvyRcG8zWFD4s0pfgeTpAlvtlgLRAGU2kqCYE';

export { BASE_URL, BEARER_TOKEN };

